import { format, type Locale } from "date-fns";
import { arSA } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import type { Control, UseFormTrigger } from "react-hook-form";
import { useI18nContext } from "~/i18n/i18n-react";
import { cn } from "~/utils/cn";
import useCalendarLocale from "~/utils/use-calendar-locale";
import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { TimePicker12Demo } from "../../ui/time-picker-demo";
import type { CreateRoomFormValues } from "./create-room-schema";

const NU_LOCALE = "ar-u-nu-arab";

const formatDay = (day: Date): string =>
	day.getDate().toLocaleString(NU_LOCALE);

const formatWeekNumber = (weekNumber: number): string =>
	weekNumber.toLocaleString(NU_LOCALE);

const formatMonthCaption = (date: Date, options?: { locale?: Locale }) => {
	const year = date.getFullYear().toLocaleString(NU_LOCALE);
	const month = format(date, "LLLL", options);
	return `${month} ${year}`;
};

interface DateTimeFieldProps {
	control: Control<CreateRoomFormValues>;
	name: "startTime" | "endTime";
	label: string;
	/** Earliest selectable day. */
	fromDate: Date;
	/** Re-validates the end/start relationship after any change. */
	trigger: UseFormTrigger<CreateRoomFormValues>;
	/** Whether an end time has been chosen yet. */
	hasEndTime: boolean;
	/** Optional callback when date changes. */
	onDateChange?: (value?: Date) => void;
}

/**
 * A calendar-plus-time picker bound to one date field of the create-room form.
 * Shared by the start and end fields, which are otherwise identical.
 */
export const DateTimeField = ({
	control,
	name,
	label,
	fromDate,
	trigger,
	hasEndTime,
	onDateChange,
}: DateTimeFieldProps) => {
	const { LL } = useI18nContext();
	const calendarLocale = useCalendarLocale();
	const isArabic = calendarLocale === arSA;

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => {
				const handleCalendarSelect = (selectedDay?: Date) => {
					if (!selectedDay) {
						field.onChange(undefined);
						if (onDateChange) {
							onDateChange(undefined);
						}
						if (hasEndTime) {
							trigger("endTime");
						}
						return;
					}

					const newDate = new Date(selectedDay);
					if (field.value) {
						newDate.setHours(
							field.value.getHours(),
							field.value.getMinutes(),
							field.value.getSeconds(),
							field.value.getMilliseconds()
						);
					} else if (fromDate) {
						newDate.setHours(
							fromDate.getHours(),
							fromDate.getMinutes(),
							fromDate.getSeconds(),
							fromDate.getMilliseconds()
						);
					}
					if (fromDate && newDate.getTime() < fromDate.getTime()) {
						newDate.setTime(fromDate.getTime());
					}
					field.onChange(newDate);
					if (onDateChange) {
						onDateChange(newDate);
					}
					if (hasEndTime) {
						trigger("endTime");
					}
				};

				const handleTimeSelect = (value?: Date) => {
					let nextValue = value;
					if (
						fromDate &&
						nextValue &&
						nextValue.getTime() < fromDate.getTime()
					) {
						nextValue = new Date(fromDate);
					}
					field.onChange(nextValue);
					if (onDateChange) {
						onDateChange(nextValue);
					}
					if (hasEndTime) {
						trigger("endTime");
					}
				};

				return (
					<FormItem className="flex flex-col text-sm">
						<FormLabel>
							{label}
							{"*"}
						</FormLabel>
						<Popover>
							<PopoverTrigger asChild className="px-2">
								<FormControl>
									<Button
										className={cn(
											"justify-start text-start font-normal",
											!field.value && "text-muted-foreground"
										)}
										variant="outline"
									>
										<CalendarIcon className="me-2 h-4 w-4" />
										{field.value ? (
											format(field.value, "PP h:mm a")
										) : (
											<span>{LL.CREATE_ROOM_FORM.PICKDATE()}</span>
										)}
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0">
								<Calendar
									dir={isArabic ? "rtl" : "ltr"}
									formatters={
										isArabic
											? { formatDay, formatMonthCaption, formatWeekNumber }
											: undefined
									}
									fromDate={fromDate}
									initialFocus
									locale={calendarLocale}
									mode="single"
									onSelect={handleCalendarSelect}
									selected={field.value}
								/>
								<div className="border-border border-t p-3">
									<TimePicker12Demo
										date={field.value}
										minDate={fromDate}
										setDate={handleTimeSelect}
									/>
								</div>
							</PopoverContent>
						</Popover>
						<FormMessage />
					</FormItem>
				);
			}}
		/>
	);
};
